/*
 * Copyright (c) Microsoft Corporation.
 * Licensed under the MIT License.
 *
 * Code generated by Microsoft (R) AutoRest Code Generator.
 * Changes may cause incorrect behavior and will be lost if the code is regenerated.
 */

import { PagedAsyncIterableIterator, PageSettings } from "@azure/core-paging";
import { setContinuationToken } from "../pagingHelper.js";
import { DedicatedHosts } from "../operationsInterfaces/index.js";
import * as coreClient from "@azure/core-client";
import * as Mappers from "../models/mappers.js";
import * as Parameters from "../models/parameters.js";
import { ComputeManagementClient } from "../computeManagementClient.js";
import {
  SimplePollerLike,
  OperationState,
  createHttpPoller,
} from "@azure/core-lro";
import { createLroSpec } from "../lroImpl.js";
import {
  DedicatedHost,
  DedicatedHostsListByHostGroupNextOptionalParams,
  DedicatedHostsListByHostGroupOptionalParams,
  DedicatedHostsListByHostGroupResponse,
  DedicatedHostsListAvailableSizesNextOptionalParams,
  DedicatedHostsListAvailableSizesOptionalParams,
  DedicatedHostsListAvailableSizesResponse,
  DedicatedHostsGetOptionalParams,
  DedicatedHostsGetResponse,
  DedicatedHostsCreateOrUpdateOptionalParams,
  DedicatedHostsCreateOrUpdateResponse,
  DedicatedHostUpdate,
  DedicatedHostsUpdateOptionalParams,
  DedicatedHostsUpdateResponse,
  DedicatedHostsDeleteOptionalParams,
  DedicatedHostsRedeployOptionalParams,
  DedicatedHostsRedeployResponse,
  DedicatedHostsRestartOptionalParams,
  DedicatedHostsRestartResponse,
  DedicatedHostsListByHostGroupNextResponse,
  DedicatedHostsListAvailableSizesNextResponse,
} from "../models/index.js";

/// <reference lib="esnext.asynciterable" />
/** Class containing DedicatedHosts operations. */
export class DedicatedHostsImpl implements DedicatedHosts {
  private readonly client: ComputeManagementClient;

  /**
   * Initialize a new instance of the class DedicatedHosts class.
   * @param client Reference to the service client
   */
  constructor(client: ComputeManagementClient) {
    this.client = client;
  }

  /**
   * Lists all of the dedicated hosts in the specified dedicated host group. Use the nextLink property in
   * the response to get the next page of dedicated hosts.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param hostGroupName The name of the dedicated host group.
   * @param options The options parameters.
   */
  public listByHostGroup(
    resourceGroupName: string,
    hostGroupName: string,
    options?: DedicatedHostsListByHostGroupOptionalParams,
  ): PagedAsyncIterableIterator<DedicatedHost> {
    const iter = this.listByHostGroupPagingAll(
      resourceGroupName,
      hostGroupName,
      options,
    );
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listByHostGroupPagingPage(
          resourceGroupName,
          hostGroupName,
          options,
          settings,
        );
      },
    };
  }

  private async *listByHostGroupPagingPage(
    resourceGroupName: string,
    hostGroupName: string,
    options?: DedicatedHostsListByHostGroupOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<DedicatedHost[]> {
    let result: DedicatedHostsListByHostGroupResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._listByHostGroup(
        resourceGroupName,
        hostGroupName,
        options,
      );
      let page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listByHostGroupNext(
        resourceGroupName,
        hostGroupName,
        continuationToken,
        options,
      );
      continuationToken = result.nextLink;
      let page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listByHostGroupPagingAll(
    resourceGroupName: string,
    hostGroupName: string,
    options?: DedicatedHostsListByHostGroupOptionalParams,
  ): AsyncIterableIterator<DedicatedHost> {
    for await (const page of this.listByHostGroupPagingPage(
      resourceGroupName,
      hostGroupName,
      options,
    )) {
      yield* page;
    }
  }

  /**
   * Lists all available dedicated host sizes to which the specified dedicated host can be resized. NOTE:
   * The dedicated host sizes provided can be used to only scale up the existing dedicated host.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param hostGroupName The name of the dedicated host group.
   * @param hostName The name of the dedicated host.
   * @param options The options parameters.
   */
  public listAvailableSizes(
    resourceGroupName: string,
    hostGroupName: string,
    hostName: string,
    options?: DedicatedHostsListAvailableSizesOptionalParams,
  ): PagedAsyncIterableIterator<string> {
    const iter = this.listAvailableSizesPagingAll(
      resourceGroupName,
      hostGroupName,
      hostName,
      options,
    );
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listAvailableSizesPagingPage(
          resourceGroupName,
          hostGroupName,
          hostName,
          options,
          settings,
        );
      },
    };
  }

  private async *listAvailableSizesPagingPage(
    resourceGroupName: string,
    hostGroupName: string,
    hostName: string,
    options?: DedicatedHostsListAvailableSizesOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<string[]> {
    let result: DedicatedHostsListAvailableSizesResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._listAvailableSizes(
        resourceGroupName,
        hostGroupName,
        hostName,
        options,
      );
      let page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listAvailableSizesNext(
        resourceGroupName,
        hostGroupName,
        hostName,
        continuationToken,
        options,
      );
      continuationToken = result.nextLink;
      let page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listAvailableSizesPagingAll(
    resourceGroupName: string,
    hostGroupName: string,
    hostName: string,
    options?: DedicatedHostsListAvailableSizesOptionalParams,
  ): AsyncIterableIterator<string> {
    for await (const page of this.listAvailableSizesPagingPage(
      resourceGroupName,
      hostGroupName,
      hostName,
      options,
    )) {
      yield* page;
    }
  }

  /**
   * Lists all of the dedicated hosts in the specified dedicated host group. Use the nextLink property in
   * the response to get the next page of dedicated hosts.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param hostGroupName The name of the dedicated host group.
   * @param options The options parameters.
   */
  private _listByHostGroup(
    resourceGroupName: string,
    hostGroupName: string,
    options?: DedicatedHostsListByHostGroupOptionalParams,
  ): Promise<DedicatedHostsListByHostGroupResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, hostGroupName, options },
      listByHostGroupOperationSpec,
    );
  }

  /**
   * Retrieves information about a dedicated host.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param hostGroupName The name of the dedicated host group.
   * @param hostName The name of the dedicated host.
   * @param options The options parameters.
   */
  get(
    resourceGroupName: string,
    hostGroupName: string,
    hostName: string,
    options?: DedicatedHostsGetOptionalParams,
  ): Promise<DedicatedHostsGetResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, hostGroupName, hostName, options },
      getOperationSpec,
    );
  }

  /**
   * Create or update a dedicated host .
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param hostGroupName The name of the dedicated host group.
   * @param hostName The name of the dedicated host.
   * @param parameters Parameters supplied to the Create Dedicated Host.
   * @param options The options parameters.
   */
  async beginCreateOrUpdate(
    resourceGroupName: string,
    hostGroupName: string,
    hostName: string,
    parameters: DedicatedHost,
    options?: DedicatedHostsCreateOrUpdateOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<DedicatedHostsCreateOrUpdateResponse>,
      DedicatedHostsCreateOrUpdateResponse
    >
  > {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<DedicatedHostsCreateOrUpdateResponse> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined =
        undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: { resourceGroupName, hostGroupName, hostName, parameters, options },
      spec: createOrUpdateOperationSpec,
    });
    const poller = await createHttpPoller<
      DedicatedHostsCreateOrUpdateResponse,
      OperationState<DedicatedHostsCreateOrUpdateResponse>
    >(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
      resourceLocationConfig: "location",
    });
    await poller.poll();
    return poller;
  }

  /**
   * Create or update a dedicated host .
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param hostGroupName The name of the dedicated host group.
   * @param hostName The name of the dedicated host.
   * @param parameters Parameters supplied to the Create Dedicated Host.
   * @param options The options parameters.
   */
  async beginCreateOrUpdateAndWait(
    resourceGroupName: string,
    hostGroupName: string,
    hostName: string,
    parameters: DedicatedHost,
    options?: DedicatedHostsCreateOrUpdateOptionalParams,
  ): Promise<DedicatedHostsCreateOrUpdateResponse> {
    const poller = await this.beginCreateOrUpdate(
      resourceGroupName,
      hostGroupName,
      hostName,
      parameters,
      options,
    );
    return poller.pollUntilDone();
  }

  /**
   * Update a dedicated host .
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param hostGroupName The name of the dedicated host group.
   * @param hostName The name of the dedicated host.
   * @param parameters Parameters supplied to the Update Dedicated Host operation.
   * @param options The options parameters.
   */
  async beginUpdate(
    resourceGroupName: string,
    hostGroupName: string,
    hostName: string,
    parameters: DedicatedHostUpdate,
    options?: DedicatedHostsUpdateOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<DedicatedHostsUpdateResponse>,
      DedicatedHostsUpdateResponse
    >
  > {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<DedicatedHostsUpdateResponse> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined =
        undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: { resourceGroupName, hostGroupName, hostName, parameters, options },
      spec: updateOperationSpec,
    });
    const poller = await createHttpPoller<
      DedicatedHostsUpdateResponse,
      OperationState<DedicatedHostsUpdateResponse>
    >(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
      resourceLocationConfig: "location",
    });
    await poller.poll();
    return poller;
  }

  /**
   * Update a dedicated host .
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param hostGroupName The name of the dedicated host group.
   * @param hostName The name of the dedicated host.
   * @param parameters Parameters supplied to the Update Dedicated Host operation.
   * @param options The options parameters.
   */
  async beginUpdateAndWait(
    resourceGroupName: string,
    hostGroupName: string,
    hostName: string,
    parameters: DedicatedHostUpdate,
    options?: DedicatedHostsUpdateOptionalParams,
  ): Promise<DedicatedHostsUpdateResponse> {
    const poller = await this.beginUpdate(
      resourceGroupName,
      hostGroupName,
      hostName,
      parameters,
      options,
    );
    return poller.pollUntilDone();
  }

  /**
   * Delete a dedicated host.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param hostGroupName The name of the dedicated host group.
   * @param hostName The name of the dedicated host.
   * @param options The options parameters.
   */
  async beginDelete(
    resourceGroupName: string,
    hostGroupName: string,
    hostName: string,
    options?: DedicatedHostsDeleteOptionalParams,
  ): Promise<SimplePollerLike<OperationState<void>, void>> {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<void> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined =
        undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: { resourceGroupName, hostGroupName, hostName, options },
      spec: deleteOperationSpec,
    });
    const poller = await createHttpPoller<void, OperationState<void>>(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
      resourceLocationConfig: "location",
    });
    await poller.poll();
    return poller;
  }

  /**
   * Delete a dedicated host.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param hostGroupName The name of the dedicated host group.
   * @param hostName The name of the dedicated host.
   * @param options The options parameters.
   */
  async beginDeleteAndWait(
    resourceGroupName: string,
    hostGroupName: string,
    hostName: string,
    options?: DedicatedHostsDeleteOptionalParams,
  ): Promise<void> {
    const poller = await this.beginDelete(
      resourceGroupName,
      hostGroupName,
      hostName,
      options,
    );
    return poller.pollUntilDone();
  }

  /**
   * Lists all available dedicated host sizes to which the specified dedicated host can be resized. NOTE:
   * The dedicated host sizes provided can be used to only scale up the existing dedicated host.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param hostGroupName The name of the dedicated host group.
   * @param hostName The name of the dedicated host.
   * @param options The options parameters.
   */
  private _listAvailableSizes(
    resourceGroupName: string,
    hostGroupName: string,
    hostName: string,
    options?: DedicatedHostsListAvailableSizesOptionalParams,
  ): Promise<DedicatedHostsListAvailableSizesResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, hostGroupName, hostName, options },
      listAvailableSizesOperationSpec,
    );
  }

  /**
   * Redeploy the dedicated host. The operation will complete successfully once the dedicated host has
   * migrated to a new node and is running. To determine the health of VMs deployed on the dedicated host
   * after the redeploy check the Resource Health Center in the Azure Portal. Please refer to
   * https://docs.microsoft.com/azure/service-health/resource-health-overview for more details.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param hostGroupName The name of the dedicated host group.
   * @param hostName The name of the dedicated host.
   * @param options The options parameters.
   */
  async beginRedeploy(
    resourceGroupName: string,
    hostGroupName: string,
    hostName: string,
    options?: DedicatedHostsRedeployOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<DedicatedHostsRedeployResponse>,
      DedicatedHostsRedeployResponse
    >
  > {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<DedicatedHostsRedeployResponse> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined =
        undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: { resourceGroupName, hostGroupName, hostName, options },
      spec: redeployOperationSpec,
    });
    const poller = await createHttpPoller<
      DedicatedHostsRedeployResponse,
      OperationState<DedicatedHostsRedeployResponse>
    >(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
      resourceLocationConfig: "location",
    });
    await poller.poll();
    return poller;
  }

  /**
   * Redeploy the dedicated host. The operation will complete successfully once the dedicated host has
   * migrated to a new node and is running. To determine the health of VMs deployed on the dedicated host
   * after the redeploy check the Resource Health Center in the Azure Portal. Please refer to
   * https://docs.microsoft.com/azure/service-health/resource-health-overview for more details.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param hostGroupName The name of the dedicated host group.
   * @param hostName The name of the dedicated host.
   * @param options The options parameters.
   */
  async beginRedeployAndWait(
    resourceGroupName: string,
    hostGroupName: string,
    hostName: string,
    options?: DedicatedHostsRedeployOptionalParams,
  ): Promise<DedicatedHostsRedeployResponse> {
    const poller = await this.beginRedeploy(
      resourceGroupName,
      hostGroupName,
      hostName,
      options,
    );
    return poller.pollUntilDone();
  }

  /**
   * Restart the dedicated host. The operation will complete successfully once the dedicated host has
   * restarted and is running. To determine the health of VMs deployed on the dedicated host after the
   * restart check the Resource Health Center in the Azure Portal. Please refer to
   * https://docs.microsoft.com/azure/service-health/resource-health-overview for more details.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param hostGroupName The name of the dedicated host group.
   * @param hostName The name of the dedicated host.
   * @param options The options parameters.
   */
  async beginRestart(
    resourceGroupName: string,
    hostGroupName: string,
    hostName: string,
    options?: DedicatedHostsRestartOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<DedicatedHostsRestartResponse>,
      DedicatedHostsRestartResponse
    >
  > {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<DedicatedHostsRestartResponse> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined =
        undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: { resourceGroupName, hostGroupName, hostName, options },
      spec: restartOperationSpec,
    });
    const poller = await createHttpPoller<
      DedicatedHostsRestartResponse,
      OperationState<DedicatedHostsRestartResponse>
    >(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
      resourceLocationConfig: "location",
    });
    await poller.poll();
    return poller;
  }

  /**
   * Restart the dedicated host. The operation will complete successfully once the dedicated host has
   * restarted and is running. To determine the health of VMs deployed on the dedicated host after the
   * restart check the Resource Health Center in the Azure Portal. Please refer to
   * https://docs.microsoft.com/azure/service-health/resource-health-overview for more details.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param hostGroupName The name of the dedicated host group.
   * @param hostName The name of the dedicated host.
   * @param options The options parameters.
   */
  async beginRestartAndWait(
    resourceGroupName: string,
    hostGroupName: string,
    hostName: string,
    options?: DedicatedHostsRestartOptionalParams,
  ): Promise<DedicatedHostsRestartResponse> {
    const poller = await this.beginRestart(
      resourceGroupName,
      hostGroupName,
      hostName,
      options,
    );
    return poller.pollUntilDone();
  }

  /**
   * ListByHostGroupNext
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param hostGroupName The name of the dedicated host group.
   * @param nextLink The nextLink from the previous successful call to the ListByHostGroup method.
   * @param options The options parameters.
   */
  private _listByHostGroupNext(
    resourceGroupName: string,
    hostGroupName: string,
    nextLink: string,
    options?: DedicatedHostsListByHostGroupNextOptionalParams,
  ): Promise<DedicatedHostsListByHostGroupNextResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, hostGroupName, nextLink, options },
      listByHostGroupNextOperationSpec,
    );
  }

  /**
   * ListAvailableSizesNext
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param hostGroupName The name of the dedicated host group.
   * @param hostName The name of the dedicated host.
   * @param nextLink The nextLink from the previous successful call to the ListAvailableSizes method.
   * @param options The options parameters.
   */
  private _listAvailableSizesNext(
    resourceGroupName: string,
    hostGroupName: string,
    hostName: string,
    nextLink: string,
    options?: DedicatedHostsListAvailableSizesNextOptionalParams,
  ): Promise<DedicatedHostsListAvailableSizesNextResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, hostGroupName, hostName, nextLink, options },
      listAvailableSizesNextOperationSpec,
    );
  }
}
// Operation Specifications
const serializer = coreClient.createSerializer(Mappers, /* isXml */ false);

const listByHostGroupOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Compute/hostGroups/{hostGroupName}/hosts",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.DedicatedHostListResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.hostGroupName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const getOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Compute/hostGroups/{hostGroupName}/hosts/{hostName}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.DedicatedHost,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion, Parameters.expand3],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.hostGroupName,
    Parameters.hostName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const createOrUpdateOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Compute/hostGroups/{hostGroupName}/hosts/{hostName}",
  httpMethod: "PUT",
  responses: {
    200: {
      bodyMapper: Mappers.DedicatedHost,
    },
    201: {
      bodyMapper: Mappers.DedicatedHost,
    },
    202: {
      bodyMapper: Mappers.DedicatedHost,
    },
    204: {
      bodyMapper: Mappers.DedicatedHost,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  requestBody: Parameters.parameters32,
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.hostGroupName,
    Parameters.hostName,
  ],
  headerParameters: [Parameters.accept, Parameters.contentType],
  mediaType: "json",
  serializer,
};
const updateOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Compute/hostGroups/{hostGroupName}/hosts/{hostName}",
  httpMethod: "PATCH",
  responses: {
    200: {
      bodyMapper: Mappers.DedicatedHost,
      headersMapper: Mappers.DedicatedHostsUpdateHeaders,
    },
    201: {
      bodyMapper: Mappers.DedicatedHost,
      headersMapper: Mappers.DedicatedHostsUpdateHeaders,
    },
    202: {
      bodyMapper: Mappers.DedicatedHost,
      headersMapper: Mappers.DedicatedHostsUpdateHeaders,
    },
    204: {
      bodyMapper: Mappers.DedicatedHost,
      headersMapper: Mappers.DedicatedHostsUpdateHeaders,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  requestBody: Parameters.parameters33,
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.hostGroupName,
    Parameters.hostName,
  ],
  headerParameters: [Parameters.accept, Parameters.contentType],
  mediaType: "json",
  serializer,
};
const deleteOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Compute/hostGroups/{hostGroupName}/hosts/{hostName}",
  httpMethod: "DELETE",
  responses: {
    200: {},
    201: {},
    202: {},
    204: {},
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.hostGroupName,
    Parameters.hostName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listAvailableSizesOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Compute/hostGroups/{hostGroupName}/hosts/{hostName}/hostSizes",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.DedicatedHostSizeListResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.hostGroupName,
    Parameters.hostName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const redeployOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Compute/hostGroups/{hostGroupName}/hosts/{hostName}/redeploy",
  httpMethod: "POST",
  responses: {
    200: {
      headersMapper: Mappers.DedicatedHostsRedeployHeaders,
    },
    201: {
      headersMapper: Mappers.DedicatedHostsRedeployHeaders,
    },
    202: {
      headersMapper: Mappers.DedicatedHostsRedeployHeaders,
    },
    204: {
      headersMapper: Mappers.DedicatedHostsRedeployHeaders,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.hostGroupName,
    Parameters.hostName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const restartOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Compute/hostGroups/{hostGroupName}/hosts/{hostName}/restart",
  httpMethod: "POST",
  responses: {
    200: {
      headersMapper: Mappers.DedicatedHostsRestartHeaders,
    },
    201: {
      headersMapper: Mappers.DedicatedHostsRestartHeaders,
    },
    202: {
      headersMapper: Mappers.DedicatedHostsRestartHeaders,
    },
    204: {
      headersMapper: Mappers.DedicatedHostsRestartHeaders,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.hostGroupName,
    Parameters.hostName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listByHostGroupNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.DedicatedHostListResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  urlParameters: [
    Parameters.$host,
    Parameters.nextLink,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.hostGroupName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listAvailableSizesNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.DedicatedHostSizeListResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  urlParameters: [
    Parameters.$host,
    Parameters.nextLink,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.hostGroupName,
    Parameters.hostName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
